import type { ContextKey } from '@/context/useFilters/types';
import type { ProcessApiNode } from '@/lib';

export type FilterProcessesProps = {
  context?: ContextKey;
  showOnlyActive?: boolean;
  maxTagCount?: number;
  full?: boolean;
  disabled?: boolean;
};

export const isDefinedValue = (v: unknown): v is string | number => typeof v === 'string' || typeof v === 'number';

export const isObject = (v: unknown): v is Record<string, unknown> => typeof v === 'object' && v !== null;
export const hasResults = (v: unknown): v is { results: ProcessApiNode[] } =>
  isObject(v) && Array.isArray((v as Record<string, unknown>).results);
export const hasData = (v: unknown): v is { data: ProcessApiNode[] } =>
  isObject(v) && Array.isArray((v as Record<string, unknown>).data);

export function normalizeProcesses(res: unknown): ProcessApiNode[] {
  if (Array.isArray(res)) return res as ProcessApiNode[];
  if (hasResults(res)) return res.results;
  if (hasData(res)) return res.data;
  return [];
}

export type CodeTree = {
  value: string | number;
  label: string;
  children: CodeTree[];
  selectable?: boolean;
  disableCheckbox?: boolean;
};

export type BuildTreeResult = {
  treeData: CodeTree[];
  valueToLeafIds: Map<string | number, number[]>;
};

export function buildProcessTree(processes: ProcessApiNode[], parentPath = ''): BuildTreeResult {
  const valueToLeafIds = new Map<string | number, number[]>();

  const walk = (nodes: ProcessApiNode[], path: string): CodeTree[] => {
    return nodes.map((n) => {
      const nextPath = path ? `${path}/${n.code}` : n.code;

      const children = Array.isArray(n.children) ? n.children : [];
      const hasChildren = children.length > 0;

      // leaf => value is numeric id
      if (!hasChildren && typeof n.id === 'number') {
        valueToLeafIds.set(n.id, [n.id]);
        return {
          value: n.id,
          label: `${n.code} - ${n.name}`,
          children: [],
        };
      }

      // parent/group => synthetic value, non-selectable if it has no descendant leaves
      const parentValue = `path:${nextPath}`;
      const childTree = walk(children, nextPath);

      const descendantLeafIds = childTree
        .flatMap((c) => valueToLeafIds.get(c.value) ?? [])
        .filter((v): v is number => typeof v === 'number');

      valueToLeafIds.set(parentValue, descendantLeafIds);

      return {
        value: parentValue,
        label: `${!n.code.startsWith('PG') ? `${n.code} - ` : ''}${n.name}`,
        children: childTree,
        selectable: descendantLeafIds.length > 0,
        disableCheckbox: descendantLeafIds.length === 0,
      };
    });
  };

  const treeData = walk(processes, parentPath);
  return { treeData, valueToLeafIds };
}

export function uniqueNums(arr: number[]) {
  return Array.from(new Set(arr));
}
